import { useCallback, useEffect, useState } from "react";

// Kurumsal mobil uygulamanin canli durum akisi (device-status-watch.handlers).
// Server olaylari mobil ile ayni: { success, data: { serialNo, device } }.
const EVENTS = {
  deviceCupsUpdate: "cups",
  devicePotsUpdate: "pots",
  deviceWaterTankUpdate: "waterTank",
  devicePotProgramInfoUpdate: "potProgramInfo",
  deviceBrewingOrderLogUpdate: "brewingOrderLog",
  deviceCleaningOrderLogUpdate: "cleaningOrderLog",
};

/**
 * Operasyon durumu canli izleme. `deviceStatus:watch` ile tabletten anlik
 * durum istenir; sonra degisiklikler gelir. Server pencereyi acik tutar.
 *
 * @returns {{ status, error, data: {cups,pots,waterTank,potProgramInfo,
 *   brewingOrderLog,cleaningOrderLog}, updatedAt: number|null,
 *   machineDateTime: string|null, refresh: () => void }}
 */
export default function useDeviceStatusLive(socket, ready, serialNo) {
  const [status, setStatus] = useState("connecting");
  const [error, setError] = useState(null);
  const [data, setData] = useState({});
  const [updatedAt, setUpdatedAt] = useState(null);
  const [machineDateTime, setMachineDateTime] = useState(null);
  // İzleme başladığı an — veri gelmezse panel bir süre sonra nedenini söyler.
  const [watchingSince, setWatchingSince] = useState(null);
  // "Yenile" basıldı, makineden cevap bekleniyor (ikon döner; veri gelince ya
  // da 6 sn sonra durur).
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setData({});
    setUpdatedAt(null);
    setMachineDateTime(null);
  }, [serialNo]);

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    const handlers = Object.entries(EVENTS).map(([event, field]) => {
      const fn = (payload) => {
        const d = payload?.data;
        if (!d || d.serialNo !== serialNo || !d.device) return;
        const value = d.device[field];
        if (value === undefined) return;
        setRefreshing(false);
        setData((prev) => ({
          ...prev,
          [field]: value,
          // Türk kahvesi su depoları göz mesajıyla birlikte gelir (yeni tablet)
          ...(field === "cups" && d.device.tanks
            ? { tanks: d.device.tanks }
            : {}),
        }));
        setUpdatedAt(Date.now());
        if (d.device.machineDateTime)
          setMachineDateTime(d.device.machineDateTime);
      };
      socket.on(event, fn);
      return [event, fn];
    });
    return () => handlers.forEach(([event, fn]) => socket.off(event, fn));
  }, [socket, serialNo]);

  useEffect(() => {
    if (!socket || !serialNo) return undefined;
    if (!ready) {
      setStatus("connecting");
      return undefined;
    }
    socket.emit("deviceStatus:watch", { serialNo }, (ack) => {
      if (ack?.ok) {
        setStatus("watching");
        setError(null);
        setWatchingSince(Date.now());
      } else {
        setStatus("error");
        setError(ack?.reason || "error");
      }
    });
    return () => {
      try {
        socket.emit("deviceStatus:unwatch", { serialNo });
      } catch (_) {}
    };
  }, [socket, ready, serialNo]);

  useEffect(() => {
    if (!refreshing) return undefined;
    const t = setTimeout(() => setRefreshing(false), 6000);
    return () => clearTimeout(t);
  }, [refreshing]);

  const refresh = useCallback(() => {
    if (socket && ready && serialNo) {
      setRefreshing(true);
      setWatchingSince(Date.now());
      socket.emit("deviceStatus:refresh", { serialNo }, () => {});
    }
  }, [socket, ready, serialNo]);

  return {
    status,
    error,
    data,
    updatedAt,
    machineDateTime,
    watchingSince,
    refresh,
  };
}
